import React, { useContext, useState, useCallback, useEffect } from 'react';
import Navbar from './Navbar'; // NEW
import { AuthContext } from './../AuthContext';
import axios from 'axios';
import '../styles/CreditCards.css';
import { SERVER_URL } from './../config';

const CreditCards = () => {
  const { token, isAuthenticated } = useContext(AuthContext);
  const [creditCards, setCreditCards] = useState([]);
  const [filteredCreditCards, setFilteredCreditCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [editableCard, setEditableCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [newCard, setNewCard] = useState({
    name: '',
    number: '',
    balance: '',
    credit_limit: '',
    available_amount: '',
    min_payment: '',
    // Preferred
    due_day_of_month: '',
    // Legacy (optional)
    due_date: '',
    apr: '',
    active: true,
    autopay: false,
    notes: '',
  });

  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const fetchCreditCards = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.get(`${SERVER_URL}/creditcards/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (Array.isArray(response.data)) {
        setCreditCards(response.data);
      } else {
        console.error('Unexpected response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching credit cards:', error);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCreditCards();
    }
  }, [isAuthenticated, fetchCreditCards]);

  const applyFilters = useCallback(() => {
    const filtered = creditCards.filter((card) => {
      const activeMatch = (showActive && card.active) || (showInactive && !card.active);
      return activeMatch;
    });
    setFilteredCreditCards(filtered);
  }, [creditCards, showActive, showInactive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    switch (name) {
      case 'showActive':
        setShowActive(checked);
        break;
      case 'showInactive':
        setShowInactive(checked);
        break;
      default:
        break;
    }
  };

  const openModal = (card) => {
    setSelectedCard(card);
    setEditableCard({ ...card });
    setUpdateMessage('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedCard(null);
    setEditableCard(null);
    setUpdateMessage('');
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setEditableCard((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleUpdate = async () => {
    if (!selectedCard || !token || !isAuthenticated) return;
    try {
      const response = await axios.patch(
        `${SERVER_URL}/creditcards/${selectedCard.id}/`,
        editableCard,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setCreditCards((prevCards) =>
        prevCards.map((card) =>
          card.id === selectedCard.id ? response.data : card
        )
      );
      setUpdateMessage('Credit card updated successfully.');
    } catch (error) {
      console.error('Error updating credit card:', error);
      setUpdateMessage('Failed to update credit card. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedCard || !token || !isAuthenticated) return;
    try {
      await axios.delete(`${SERVER_URL}/creditcards/${selectedCard.id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setCreditCards((prevCards) =>
        prevCards.filter((card) => card.id !== selectedCard.id)
      );
      closeModal();
    } catch (error) {
      console.error('Error deleting credit card:', error);
    }
  };

  const handleCreateCard = async (e) => {
    e.preventDefault();
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.post(`${SERVER_URL}/creditcards/`, newCard, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setCreditCards([...creditCards, response.data]);
      setShowCreateModal(false);
      setNewCard({
        name: '',
        number: '',
        balance: '',
        credit_limit: '',
        available_amount: '',
        min_payment: '',
        due_day_of_month: '',
        due_date: '',
        apr: '',
        active: true,
        autopay: false,
        notes: '',
      });
    } catch (error) {
      console.error('Error creating credit card:', error);
    }
  };

  const handleNewInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setNewCard((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    return formatter.format(date);
  };

  const getDueDay = (card) => {
    if (card?.due_day_of_month !== null && card?.due_day_of_month !== undefined && card?.due_day_of_month !== '') {
      return card.due_day_of_month;
    }
    if (card?.due_date) {
      const parts = String(card.due_date).split('-');
      if (parts.length === 3) return parseInt(parts[2], 10);
    }
    return '';
  };

  // Calculate total balance and total min payment
  const totalBalance = filteredCreditCards.reduce((sum, card) => sum + parseFloat(card.balance || 0), 0);
  const totalMinPayment = filteredCreditCards.reduce((sum, card) => sum + parseFloat(card.min_payment || 0), 0);

  return (
    <div className="creditcards">
      <Navbar />
      <div className="content">
        <h1>Credit Cards</h1>
        <button className="create-creditcard-button" onClick={() => setShowCreateModal(true)}>
          Add Credit Card
        </button>
        <div className="filter-options">
          <label>
            <input
              type="checkbox"
              name="showActive"
              checked={showActive}
              onChange={handleFilterChange}
            />
            Show Active
          </label>
          <label>
            <input
              type="checkbox"
              name="showInactive"
              checked={showInactive}
              onChange={handleFilterChange}
            />
            Show Inactive
          </label>
        </div>
        <div className="creditcards-container">
          <table className="creditcards-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Balance</th>
                <th>Credit Limit</th>
                <th>Available Amount</th>
                <th>Min Payment</th>
                <th>Due Day</th>
                <th>Due Date (legacy)</th>
                <th>APR</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {filteredCreditCards.map((card, index) => (
                <tr
                  key={card.id}
                  className={index % 2 === 0 ? 'even' : 'odd'}
                  onClick={() => openModal(card)}
                >
                  <td>{card.name}</td>
                  <td>{card.number}</td>
                  <td>{card.balance}</td>
                  <td>{card.credit_limit}</td>
                  <td>{card.available_amount}</td>
                  <td>{card.min_payment}</td>
                  <td>{getDueDay(card) || 'N/A'}</td>
                  <td>{card.due_date ? formatDate(card.due_date) : '—'}</td>
                  <td>{card.apr}</td>
                  <td>{card.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td></td>
                <td><strong>${totalBalance.toFixed(2)}</strong></td>
                <td></td>
                <td></td>
                <td><strong>${totalMinPayment.toFixed(2)}</strong></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <div className="creditcards-tiles">
            {filteredCreditCards.map((card) => (
              <div
                key={card.id}
                className="creditcard-tile"
                onClick={() => openModal(card)}
              >
                <div className="creditcard-name">{card.name}</div>
                <div className="creditcard-number">
                  <label>Number: </label>
                  {card.number}
                </div>
                <div className="creditcard-balance">
                  <label>Balance: </label>
                  {card.balance}
                </div>
                <div className="creditcard-credit-limit">
                  <label>Credit Limit: </label>
                  {card.credit_limit}
                </div>
                <div className="creditcard-available-amount">
                  <label>Available Amount: </label>
                  {card.available_amount}
                </div>
                <div className="creditcard-min-payment">
                  <label>Min Payment: </label>
                  {card.min_payment}
                </div>
                <div className="creditcard-due-date">
                  <label>Due Day: </label>
                  {getDueDay(card) || 'N/A'}
                </div>
                <div className="creditcard-due-date">
                  <label>Due Date (legacy): </label>
                  {card.due_date ? formatDate(card.due_date) : '—'}
                </div>
                <div className="creditcard-apr">
                  <label>APR: </label>
                  {card.apr}
                </div>
                <div className="creditcard-active">
                  <label>Active: </label>
                  {card.active ? 'Yes' : 'No'}
                </div>
              </div>
            ))}
          </div>
        </div>
        {isModalOpen && editableCard && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Edit Credit Card</h2>
              <form>
                <label>
                  Name:
                  <input
                    type="text"
                    name="name"
                    value={editableCard.name}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Number:
                  <input
                    type="text"
                    name="number"
                    value={editableCard.number}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Balance:
                  <input
                    type="number"
                    name="balance"
                    value={editableCard.balance}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Credit Limit:
                  <input
                    type="number"
                    name="credit_limit"
                    value={editableCard.credit_limit}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Available Amount:
                  <input
                    type="number"
                    name="available_amount"
                    value={editableCard.available_amount}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Min Payment:
                  <input
                    type="number"
                    name="min_payment"
                    value={editableCard.min_payment}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Due Day (1-31):
                  <input
                    type="number"
                    name="due_day_of_month"
                    min="1"
                    max="31"
                    value={editableCard.due_day_of_month ?? getDueDay(editableCard) ?? ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Due Date (legacy):
                  <input
                    type="date"
                    name="due_date"
                    value={editableCard.due_date || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  APR:
                  <input
                    type="number"
                    name="apr"
                    value={editableCard.apr}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Notes:
                  <textarea
                    name="notes"
                    value={editableCard.notes}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={editableCard.active}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Autopay:
                  <input
                    type="checkbox"
                    name="autopay"
                    checked={editableCard.autopay}
                    onChange={handleInputChange}
                  />
                </label>
              </form>
              {updateMessage && (
                <div
                  className={`modal-message ${
                    updateMessage.includes('success') ? 'success' : 'error'
                  }`}
                >
                  {updateMessage}
                </div>
              )}
              <div className="modal-buttons">
                <button className="modal-button" onClick={handleUpdate}>
                  Update
                </button>
                <button className="modal-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
                <button className="modal-button close-button" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showCreateModal && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={() => setShowCreateModal(false)}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Create Credit Card</h2>
              <form onSubmit={handleCreateCard}>
                <label>
                  Name:
                  <input
                    type="text"
                    name="name"
                    value={newCard.name}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Number:
                  <input
                    type="text"
                    name="number"
                    value={newCard.number}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Balance:
                  <input
                    type="number"
                    name="balance"
                    value={newCard.balance}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Credit Limit:
                  <input
                    type="number"
                    name="credit_limit"
                    value={newCard.credit_limit}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Available Amount:
                  <input
                    type="number"
                    name="available_amount"
                    value={newCard.available_amount}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Min Payment:
                  <input
                    type="number"
                    name="min_payment"
                    value={newCard.min_payment}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Due Day (1-31):
                  <input
                    type="number"
                    name="due_day_of_month"
                    min="1"
                    max="31"
                    value={newCard.due_day_of_month}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label>
                  Due Date (legacy):
                  <input
                    type="date"
                    name="due_date"
                    value={newCard.due_date}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label>
                  APR:
                  <input
                    type="number"
                    name="apr"
                    value={newCard.apr}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Notes:
                  <textarea
                    name="notes"
                    value={newCard.notes}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={newCard.active}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Autopay:
                  <input
                    type="checkbox"
                    name="autopay"
                    checked={newCard.autopay}
                    onChange={handleNewInputChange}
                  />
                </label>
                <button type="submit">Create</button>
              </form>
              <button
                className="modal-button close-button"
                onClick={() => setShowCreateModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditCards;
